import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Info, Database, RefreshCw, Download, BookOpen, HelpCircle, Search,
  TrendingUp, TrendingDown, Minus, AlertTriangle, ChevronLeft, ChevronRight,
  ArrowUpDown, ArrowUp, ArrowDown, Columns3, Eye, EyeOff
} from "lucide-react";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/risk-score-v2`;

const RISK_CLASS_COLORS = { 1: "#16A34A", 2: "#65A30D", 3: "#EAB308", 4: "#EA580C", 5: "#DC2626" };
const RISK_CLASS_LABELS = { 1: "Lowest Risk", 2: "Low Risk", 3: "Moderate Risk", 4: "Elevated Risk", 5: "Highest Risk" };

const fmtCurrency = (v) => {
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const OPTIONAL_COLS = [
  { key: "bureau_score", label: "Bureau Score" },
  { key: "payment_behavior_score", label: "Payment Behavior" },
  { key: "exposure_capacity_score", label: "Exposure & Capacity" },
  { key: "business_health_score", label: "Business Health" },
  { key: "trend_momentum_score", label: "Trend & Momentum" },
  { key: "ecl", label: "Expected Credit Loss" },
  { key: "watchlist", label: "Watchlist Flag" },
  { key: "region", label: "Region" },
  { key: "industry", label: "Industry" },
];

export default function RiskScoreDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [glossary, setGlossary] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAnalystView, setIsAnalystView] = useState(false);
  const [showGlossary, setShowGlossary] = useState(false);
  const [selectedMetricKey, setSelectedMetricKey] = useState(null);
  const [metricInfo, setMetricInfo] = useState(null);
  const [glossarySearch, setGlossarySearch] = useState("");
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [visibleOptional, setVisibleOptional] = useState(new Set());

  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("composite_score");
  const [sortDir, setSortDir] = useState("asc");

  const [filters, setFilters] = useState({
    search: "", risk_class: "all", score_band: "all", score_change: "all",
    watchlist: "all", trigger_type: "all", segment: "all", industry: "all",
    region: "all", dpd_bucket: "all", due_next_7d_flag: "all", failed_debit: "all",
    action_needed: "all", owner: "all",
  });

  const setFilter = (key, val) => { setFilters(f => ({ ...f, [key]: val })); setPage(1); };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 25, sort_by: sortBy, sort_dir: sortDir });
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params.append(k, v); });
      const [listRes, glossaryRes] = await Promise.all([
        axios.get(`${API}/customers?${params}`),
        axios.get(`${API}/glossary`)
      ]);
      setData(listRes.data);
      setGlossary(glossaryRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [page, sortBy, sortDir, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openMetricInfo = async (key) => {
    setSelectedMetricKey(key);
    try {
      const res = await axios.get(`${API}/metrics/${key}`);
      setMetricInfo(res.data);
    } catch { setMetricInfo({ title: key, definition: "Info not available." }); }
  };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
    setPage(1);
  };

  const toggleRow = (id) => setSelectedRows(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const exportCSV = () => {
    if (!data?.customers) return;
    const headers = ["Customer Name","Segment","Risk Class","Score","Score Change 30d","Gross Exposure","Past Due","DPD","First Attempt Success","Recommended Action","Owner"];
    const rows = data.customers.map(c => [c.customer_name, c.segment, RISK_CLASS_LABELS[c.risk_class], c.composite_score, c.score_change_30d, c.gross_exposure, c.past_due, c.dpd, c.first_attempt_success, c.recommended_action || "", c.owner]);
    const csv = [headers.join(","), ...rows.map(r => r.map(v => `"${v}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "risk_score_class.csv"; a.click();
  };

  const totalPages = data ? Math.ceil(data.total_count / 25) : 0;
  const summary = data?.summary;

  const SortHeader = ({ col, children, className }) => (
    <TableHead className={cn("cursor-pointer select-none hover:bg-gray-50 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider whitespace-nowrap", className)} onClick={() => handleSort(col)}>
      <div className="flex items-center gap-1">
        {children}
        {sortBy === col ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-2.5 w-2.5 opacity-30" />}
      </div>
    </TableHead>
  );

  if (loading && !data) {
    return <div className="min-h-screen flex items-center justify-center"><RefreshCw className="h-8 w-8 animate-spin text-[#502D3C]" /></div>;
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[#F8FAFC]" data-testid="risk-score-page">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white border-b border-[#E2E8F0] px-6 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-[#502D3C] tracking-tight" data-testid="page-title">Risk Score & Class</h1>
              <Badge variant="outline" className="text-[10px] font-normal bg-gray-50 h-5">Customer Risk Queue</Badge>
              <Badge variant="outline" className="text-[10px] bg-white h-5">{new Date().toISOString().split("T")[0]}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowGlossary(true)} className="text-[#64748B] hover:text-[#502D3C] h-8 text-xs" data-testid="glossary-btn">
                <BookOpen className="h-3.5 w-3.5 mr-1" />Glossary
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={exportCSV} data-testid="export-btn"><Download className="h-3.5 w-3.5 mr-1" />Export CSV</Button>
              <Button variant="ghost" size="icon" onClick={fetchData} className="h-8 w-8" data-testid="refresh-btn"><RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} /></Button>
              <div className="ml-2 flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded text-[11px]">
                <span className={!isAnalystView ? "text-[#502D3C] font-medium" : "text-[#94A3B8]"}>Simple</span>
                <Switch checked={isAnalystView} onCheckedChange={setIsAnalystView} className="scale-75" data-testid="view-toggle" />
                <span className={isAnalystView ? "text-[#502D3C] font-medium" : "text-[#94A3B8]"}>Analyst</span>
              </div>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="filters-bar">
            <FilterSelect label="Risk Class" value={filters.risk_class} onChange={v => setFilter("risk_class", v)}
              options={[["all","All Classes"],["1","Lowest Risk"],["2","Low Risk"],["3","Moderate Risk"],["4","Elevated Risk"],["5","Highest Risk"]]} />
            <FilterSelect label="Score Band" value={filters.score_band} onChange={v => setFilter("score_band", v)}
              options={[["all","All Scores"],["85-100","85–100"],["70-84","70–84"],["55-69","55–69"],["40-54","40–54"],["0-39","0–39"]]} />
            <FilterSelect label="Score Δ" value={filters.score_change} onChange={v => setFilter("score_change", v)}
              options={[["all","Any Change"],["improved","Improved"],["stable","Stable"],["worsened","Worsened"]]} />
            <FilterSelect label="Watchlist" value={filters.watchlist} onChange={v => setFilter("watchlist", v)}
              options={[["all","All"],["yes","On Watchlist"],["no","Not on Watchlist"]]} />
            <FilterSelect label="Trigger" value={filters.trigger_type} onChange={v => setFilter("trigger_type", v)}
              options={[["all","All Triggers"],["collections","Collections"],["bureau","Bureau"],["capacity","Capacity"],["business_health","Business Health"],["trend","Trend"]]} />
            <FilterSelect label="Segment" value={filters.segment} onChange={v => setFilter("segment", v)}
              options={[["all","All Segments"],["Enterprise","Enterprise"],["Mid-Market","Mid-Market"],["SMB","SMB"]]} />
            <FilterSelect label="Industry" value={filters.industry} onChange={v => setFilter("industry", v)}
              options={[["all","All Industries"],["Technology","Technology"],["Manufacturing","Manufacturing"],["Healthcare","Healthcare"],["Retail","Retail"],["Financial Services","Financial Services"],["Real Estate","Real Estate"],["Hospitality","Hospitality"],["Professional Services","Professional Services"],["Energy","Energy"],["Transportation","Transportation"]]} />
            <FilterSelect label="Region" value={filters.region} onChange={v => setFilter("region", v)}
              options={[["all","All Regions"],["West","West"],["East","East"],["Midwest","Midwest"],["Southeast","Southeast"],["Northeast","Northeast"],["Southwest","Southwest"]]} />
            <FilterSelect label="DPD" value={filters.dpd_bucket} onChange={v => setFilter("dpd_bucket", v)}
              options={[["all","All DPD"],["current","Current"],["1-14","1–14d"],["15-29","15–29d"],["30-59","30–59d"],["60+","60+ d"]]} />
            <FilterSelect label="Due 7d" value={filters.due_next_7d_flag} onChange={v => setFilter("due_next_7d_flag", v)}
              options={[["all","All"],["yes","Has Dues"]]} />
            <FilterSelect label="Failed Debit" value={filters.failed_debit} onChange={v => setFilter("failed_debit", v)}
              options={[["all","All"],["yes","Has Failed"]]} />
            <FilterSelect label="Action" value={filters.action_needed} onChange={v => setFilter("action_needed", v)}
              options={[["all","All"],["yes","Needs Action"]]} />
            <FilterSelect label="Owner" value={filters.owner} onChange={v => setFilter("owner", v)}
              options={[["all","All Owners"],["Sarah Chen","Sarah Chen"],["Michael Park","Michael Park"],["Jessica Liu","Jessica Liu"],["David Kim","David Kim"],["Rachel Torres","Rachel Torres"],["Unassigned","Unassigned"]]} />
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-[#94A3B8]" />
              <Input placeholder="Search customer..." value={filters.search} onChange={e => setFilter("search", e.target.value)}
                className="pl-7 h-7 w-[150px] bg-white text-[11px] border-gray-200" data-testid="search-input" />
            </div>
          </div>
        </header>

        <main className="p-4 space-y-4">
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-6 gap-3" data-testid="summary-cards">
              <SummaryCard title="Avg Risk Score" value={summary.avg_risk_score.toFixed(1)} delta={summary.avg_risk_score_delta} interpretation="Average composite score across portfolio" onInfo={() => openMetricInfo("avg_risk_score")} isAnalystView={isAnalystView} source="risk.customer_composite_score_daily" refreshed={summary.last_refreshed} invert />
              <SummaryCard title="Exposure in Class 4–5" value={fmtCurrency(summary.exposure_class_4_5)} subValue={`${summary.exposure_class_4_5_pct}% of book`} delta={null} interpretation="Total exposure in Elevated + Highest Risk" onInfo={() => openMetricInfo("exposure_class_4_5")} isAnalystView={isAnalystView} source="risk.customer_composite_score_daily" refreshed={summary.last_refreshed} status={summary.exposure_class_4_5_pct > 15 ? "risk" : summary.exposure_class_4_5_pct > 8 ? "watch" : "good"} />
              <SummaryCard title="Downgraded (30d)" value={summary.customers_downgraded_30d} delta={summary.customers_downgraded_delta} interpretation="Customers whose class worsened" onInfo={() => openMetricInfo("customers_downgraded_30d")} isAnalystView={isAnalystView} source="risk.customer_composite_score_daily" refreshed={summary.last_refreshed} status={summary.customers_downgraded_30d > 5 ? "risk" : summary.customers_downgraded_30d > 2 ? "watch" : "good"} />
              <SummaryCard title="Action Needed" value={summary.customers_action_needed} delta={summary.customers_action_delta} interpretation="Customers with action-triggering metrics" onInfo={() => openMetricInfo("customers_action_needed")} isAnalystView={isAnalystView} source="risk.customer_action_daily" refreshed={summary.last_refreshed} status={summary.customers_action_needed > 8 ? "risk" : summary.customers_action_needed > 4 ? "watch" : "good"} />
              <SummaryCard title="Failed Debits (30d)" value={summary.customers_failed_debits_30d} delta={summary.customers_failed_debits_delta} interpretation="Customers with failed debit attempts" onInfo={() => openMetricInfo("customers_failed_debits_30d")} isAnalystView={isAnalystView} source="finance.card_collection_attempt" refreshed={summary.last_refreshed} status={summary.customers_failed_debits_30d > 5 ? "risk" : summary.customers_failed_debits_30d > 2 ? "watch" : "good"} />
              <SummaryCard title="Watchlist" value={summary.watchlist_count} delta={summary.watchlist_delta} interpretation="Customers on credit watchlist" onInfo={() => openMetricInfo("watchlist_count")} isAnalystView={isAnalystView} source="risk.customer_composite_score_daily" refreshed={summary.last_refreshed} status={summary.watchlist_count > 8 ? "risk" : summary.watchlist_count > 4 ? "watch" : "good"} />
            </div>
          )}

          {/* Table */}
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2">
                <p className="text-xs font-medium text-[#502D3C]">{data?.total_count || 0} customers</p>
                {selectedRows.size > 0 && <Badge variant="outline" className="text-[10px] h-5">{selectedRows.size} selected</Badge>}
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 text-[11px] text-[#64748B]" data-testid="column-toggle"><Columns3 className="h-3.5 w-3.5 mr-1" />Columns</Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="end">
                  <p className="text-xs font-semibold text-[#502D3C] mb-2 px-1">Optional columns</p>
                  {OPTIONAL_COLS.map(col => (
                    <label key={col.key} className="flex items-center gap-2 px-1 py-1.5 hover:bg-gray-50 rounded cursor-pointer">
                      <Checkbox checked={visibleOptional.has(col.key)} onCheckedChange={(checked) => setVisibleOptional(prev => {
                        const next = new Set(prev);
                        checked ? next.add(col.key) : next.delete(col.key);
                        return next;
                      })} className="h-3.5 w-3.5" />
                      <span className="text-[11px] text-[#502D3C]">{col.label}</span>
                    </label>
                  ))}
                </PopoverContent>
              </Popover>
            </div>
            <div className="overflow-x-auto">
              <Table className="table-auto">
                <TableHeader className="sticky top-0 z-10 bg-white">
                  <TableRow className="border-b border-gray-200">
                    <TableHead className="w-8 px-2"><Checkbox checked={selectedRows.size === (data?.customers?.length || 0) && selectedRows.size > 0}
                      onCheckedChange={(checked) => setSelectedRows(checked ? new Set(data?.customers?.map(c => c.customer_id)) : new Set())} className="h-3.5 w-3.5" /></TableHead>
                    <SortHeader col="customer_name">Customer</SortHeader>
                    <SortHeader col="segment">Segment</SortHeader>
                    <SortHeader col="risk_class" className="min-w-[100px]">Risk Class</SortHeader>
                    <SortHeader col="composite_score">Score</SortHeader>
                    <SortHeader col="score_change_30d">Δ 30d</SortHeader>
                    <SortHeader col="gross_exposure">Exposure</SortHeader>
                    <SortHeader col="past_due">Past Due</SortHeader>
                    <SortHeader col="due_next_7d">Due 7d</SortHeader>
                    <SortHeader col="dpd">DPD</SortHeader>
                    <SortHeader col="first_attempt_success">1st Attempt</SortHeader>
                    <TableHead className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider">Triggers</TableHead>
                    <TableHead className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider">Action</TableHead>
                    <SortHeader col="owner">Owner</SortHeader>
                    <SortHeader col="last_reviewed">Reviewed</SortHeader>
                    {OPTIONAL_COLS.filter(c => visibleOptional.has(c.key)).map(col => (
                      <SortHeader key={col.key} col={col.key}>{col.label}</SortHeader>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.customers?.map(c => (
                    <TableRow key={c.customer_id} className="cursor-pointer hover:bg-[#FFCC1C]/5 transition-colors border-b border-gray-50"
                      onClick={() => navigate(`/risk-score/${c.customer_id}`)} data-testid={`row-${c.customer_id}`}>
                      <TableCell className="px-2" onClick={e => e.stopPropagation()}>
                        <Checkbox checked={selectedRows.has(c.customer_id)} onCheckedChange={() => toggleRow(c.customer_id)} className="h-3.5 w-3.5" />
                      </TableCell>
                      <TableCell className="text-xs font-medium text-[#502D3C] whitespace-nowrap">{c.customer_name}</TableCell>
                      <TableCell className="text-[11px] text-[#64748B]">{c.segment}</TableCell>
                      <TableCell>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge className="text-[10px] h-5 border whitespace-nowrap" style={{ backgroundColor: RISK_CLASS_COLORS[c.risk_class] + '15', borderColor: RISK_CLASS_COLORS[c.risk_class] + '40', color: RISK_CLASS_COLORS[c.risk_class] }}>
                              {RISK_CLASS_LABELS[c.risk_class]}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent><p className="text-[10px]">Class {c.risk_class}: Score range {c.risk_class === 1 ? '85–100' : c.risk_class === 2 ? '70–84' : c.risk_class === 3 ? '55–69' : c.risk_class === 4 ? '40–54' : '<40'}</p>
                            {c.override_reason && <p className="text-[10px] text-red-400 mt-0.5">Override: {c.override_reason}</p>}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className={cn("text-xs font-semibold", c.composite_score >= 70 ? "text-green-600" : c.composite_score >= 55 ? "text-[#502D3C]" : c.composite_score >= 40 ? "text-amber-600" : "text-red-600")}>{c.composite_score.toFixed(0)}</span>
                          </TooltipTrigger>
                          <TooltipContent><p className="text-[10px]">Composite Risk Score: {c.composite_score.toFixed(1)} / 100</p><p className="text-[10px] text-gray-400">100 = safest, 0 = riskiest</p><p className="text-[10px] text-gray-400">Confidence: {c.score_confidence}</p></TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <span className={cn("text-[11px] font-medium flex items-center gap-0.5",
                          c.score_change_30d > 2 ? "text-green-600" : c.score_change_30d < -2 ? "text-red-600" : "text-[#94A3B8]")}>
                          {c.score_change_30d > 0.5 ? <TrendingUp className="h-3 w-3" /> : c.score_change_30d < -0.5 ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                          {c.score_change_30d > 0 ? "+" : ""}{c.score_change_30d.toFixed(1)}
                        </span>
                      </TableCell>
                      <TableCell className="text-[11px] text-[#502D3C] font-medium whitespace-nowrap">{fmtCurrency(c.gross_exposure)}</TableCell>
                      <TableCell className={cn("text-[11px] font-medium whitespace-nowrap", c.past_due > 0 ? "text-red-600" : "text-[#94A3B8]")}>{c.past_due > 0 ? fmtCurrency(c.past_due) : "—"}</TableCell>
                      <TableCell className="text-[11px] text-[#64748B] whitespace-nowrap">{c.due_next_7d > 0 ? fmtCurrency(c.due_next_7d) : "—"}</TableCell>
                      <TableCell><span className={cn("text-[11px] font-medium", c.dpd >= 30 ? "text-red-600" : c.dpd >= 15 ? "text-amber-600" : c.dpd > 0 ? "text-[#502D3C]" : "text-[#94A3B8]")}>{c.dpd > 0 ? `${c.dpd}d` : "—"}</span></TableCell>
                      <TableCell className={cn("text-[11px] font-medium", c.first_attempt_success >= 90 ? "text-green-600" : c.first_attempt_success >= 75 ? "text-[#502D3C]" : "text-red-600")}>{c.first_attempt_success.toFixed(0)}%</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 flex-nowrap">
                          {c.triggers.slice(0, 2).map((t, idx) => (
                            <Tooltip key={idx}>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className={cn("text-[9px] h-4 px-1.5 whitespace-nowrap cursor-help flex-shrink-0",
                                  t.severity === "red" ? "bg-red-50 text-red-600 border-red-200" : "bg-amber-50 text-amber-600 border-amber-200")}>
                                  {t.label}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[250px]"><p className="text-[10px] font-medium">{t.label}</p><p className="text-[10px] text-gray-400 mt-0.5">{t.reason}</p></TooltipContent>
                            </Tooltip>
                          ))}
                          {c.triggers.length === 0 && <span className="text-[10px] text-[#94A3B8]">—</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {c.recommended_action ? (
                          <Badge variant="outline" className="text-[9px] h-4 px-1.5 bg-[#502D3C]/5 text-[#502D3C] border-[#502D3C]/20 whitespace-nowrap max-w-[200px] truncate inline-block leading-[14px]">{c.recommended_action}</Badge>
                        ) : <span className="text-[10px] text-[#94A3B8]">—</span>}
                      </TableCell>
                      <TableCell className="text-[11px] text-[#64748B] whitespace-nowrap">{c.owner}</TableCell>
                      <TableCell className="text-[11px] text-[#94A3B8] whitespace-nowrap">{c.last_reviewed}</TableCell>
                      {OPTIONAL_COLS.filter(col => visibleOptional.has(col.key)).map(col => (
                        <TableCell key={col.key} className="text-[11px] text-[#64748B] whitespace-nowrap">
                          {col.key === "ecl" ? fmtCurrency(c[col.key]) :
                           col.key === "watchlist" ? (c[col.key] ? <Badge variant="outline" className="text-[9px] h-4 bg-amber-50 text-amber-600 border-amber-200">Yes</Badge> : "—") :
                           typeof c[col.key] === "number" ? c[col.key].toFixed(1) : (c[col.key] || "—")}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100" data-testid="pagination">
              <p className="text-[11px] text-[#64748B]">Page {page} of {totalPages} ({data?.total_count || 0} customers)</p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft className="h-3.5 w-3.5" />Prev
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                  Next<ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </Card>
        </main>

        {/* Glossary Sheet */}
        <Sheet open={showGlossary} onOpenChange={setShowGlossary}>
          <SheetContent className="w-[400px]">
            <SheetHeader><SheetTitle className="text-[#502D3C]">Glossary</SheetTitle></SheetHeader>
            <Input placeholder="Search terms..." value={glossarySearch} onChange={e => setGlossarySearch(e.target.value)} className="mt-4 mb-3 text-xs" />
            <ScrollArea className="h-[calc(100vh-160px)]">
              <div className="space-y-3 pr-3">
                {Object.entries(glossary).filter(([term]) => term.toLowerCase().includes(glossarySearch.toLowerCase())).map(([term, def]) => (
                  <div key={term} className="p-3 bg-gray-50 rounded">
                    <h4 className="text-xs font-semibold text-[#502D3C] capitalize">{term.replace(/_/g, ' ')}</h4>
                    <p className="text-[11px] text-[#64748B] mt-1 leading-relaxed">{def}</p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </SheetContent>
        </Sheet>

        {/* Metric Info Sheet */}
        <Sheet open={!!selectedMetricKey} onOpenChange={() => setSelectedMetricKey(null)}>
          <SheetContent className="w-[400px]">
            <SheetHeader><SheetTitle className="text-[#502D3C]">{metricInfo?.title || ""}</SheetTitle></SheetHeader>
            {metricInfo && (
              <div className="space-y-4 mt-4">
                <div>
                  <h4 className="text-xs font-semibold text-[#502D3C] mb-1">What is this?</h4>
                  <p className="text-xs text-[#64748B] leading-relaxed">{metricInfo.definition}</p>
                </div>
                {metricInfo.formula && <div>
                  <h4 className="text-xs font-semibold text-[#502D3C] mb-1">Formula</h4>
                  <p className="text-xs font-mono bg-gray-50 p-2 rounded border border-gray-100">{metricInfo.formula}</p>
                </div>}
                {metricInfo.interpretation && <div>
                  <h4 className="text-xs font-semibold text-[#502D3C] mb-1">Interpretation</h4>
                  <p className="text-xs text-[#64748B] leading-relaxed">{metricInfo.interpretation}</p>
                </div>}
                <Separator />
                <div className="bg-gray-50 p-3 rounded space-y-1.5">
                  <div className="flex justify-between text-xs"><span className="text-[#64748B]">Source</span><span className="font-mono text-[10px] text-[#502D3C]">{metricInfo.source}</span></div>
                  <div className="flex justify-between text-xs"><span className="text-[#64748B]">Refresh</span><span className="text-[#502D3C]">{metricInfo.refresh}</span></div>
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </TooltipProvider>
  );
}

// ===== HELPER COMPONENTS =====

function FilterSelect({ label, value, onChange, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-7 bg-white text-[11px] border-gray-200 min-w-0 w-auto px-2" data-testid={`filter-${label.toLowerCase().replace(/\s/g,'-')}`}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        {options.map(([val, lbl]) => <SelectItem key={val} value={val} className="text-[11px]">{lbl}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function SummaryCard({ title, value, subValue, delta, interpretation, onInfo, isAnalystView, source, refreshed, status, invert }) {
  const deltaColor = delta === null ? "" : (invert ? (delta < 0 ? "text-red-600" : delta > 0 ? "text-green-600" : "text-[#94A3B8]") : (delta > 0 ? "text-red-600" : delta < 0 ? "text-green-600" : "text-[#94A3B8]"));
  const statusColor = status === "risk" ? "bg-red-500" : status === "watch" ? "bg-amber-500" : status === "good" ? "bg-green-500" : "";
  return (
    <Card className="bg-white border-gray-100 hover:shadow-sm transition-shadow" data-testid={`summary-${title.toLowerCase().replace(/\s/g,'-')}`}>
      <CardContent className="p-3">
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-1.5">
            {statusColor && <div className={cn("w-1.5 h-1.5 rounded-full", statusColor)} />}
            <p className="text-[9px] font-medium text-[#64748B] uppercase tracking-wider leading-tight">{title}</p>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <Tooltip><TooltipTrigger asChild><button onClick={onInfo} className="p-0.5 hover:bg-gray-100 rounded opacity-60 hover:opacity-100"><Info className="h-3 w-3 text-[#94A3B8]" /></button></TooltipTrigger>
              <TooltipContent><p className="text-[10px]">View details</p></TooltipContent></Tooltip>
            <Tooltip><TooltipTrigger asChild><button className="p-0.5 hover:bg-gray-100 rounded opacity-50 hover:opacity-100"><Database className="h-2.5 w-2.5 text-[#94A3B8]" /></button></TooltipTrigger>
              <TooltipContent><p className="text-[10px] font-mono">{source}</p><p className="text-[9px] text-gray-400">Last: {refreshed}</p></TooltipContent></Tooltip>
          </div>
        </div>
        <p className="text-xl font-bold text-[#502D3C] tracking-tight">{value}</p>
        {subValue && <p className="text-[10px] text-[#94A3B8]">{subValue}</p>}
        {delta !== null && delta !== undefined && (
          <div className="flex items-center gap-1 mt-0.5">
            <span className={cn("flex items-center gap-0.5 text-[10px] font-medium", deltaColor)}>
              {delta > 0 ? <TrendingUp className="h-2.5 w-2.5" /> : delta < 0 ? <TrendingDown className="h-2.5 w-2.5" /> : <Minus className="h-2.5 w-2.5" />}
              {delta > 0 ? "+" : ""}{typeof delta === 'number' ? delta.toFixed(1) : delta}
            </span>
            <span className="text-[9px] text-[#94A3B8]">vs prior</span>
          </div>
        )}
        <p className="text-[9px] text-[#94A3B8] mt-1 leading-relaxed border-t border-gray-50 pt-1">{interpretation}</p>
        {isAnalystView && <p className="text-[8px] text-[#94A3B8] mt-0.5 font-mono">{source}</p>}
      </CardContent>
    </Card>
  );
}
